/**
 * Controla somente o sticky buy configurável da Product Page.
 * A compra continua delegada ao ProductForm nativo do tema para preservar o cart drawer.
 */
if (!customElements.get('product-sticky-buy-button')) {
  class ProductStickyBuyButton extends HTMLElement {
    /** Inicializa observadores, seletores e sincronização com o formulário principal. */
    connectedCallback() {
      this.productForm = document.getElementById(this.getAttribute('form'));
      this.footer = document.querySelector('.footer-group');
      this.actionButton = this.querySelector('[data-sticky-action]');
      this.variantSelect = this.querySelector('[data-sticky-variant-select]');
      this.optionSelects = Array.from(this.querySelectorAll('[data-sticky-option-select]'));
      this.mobilePicker = this.querySelector('[data-sticky-mobile-picker]');
      this.mobilePickerPanel = this.querySelector('[data-sticky-mobile-picker-panel]');
      this.mobilePickerToggle = this.querySelector('[data-sticky-mobile-picker-toggle]');
      this.mobilePickerLabel = this.querySelector('[data-sticky-mobile-picker-label]');
      this.mobileVariantButtons = Array.from(this.querySelectorAll('[data-sticky-mobile-variant]'));
      this.buttonLabel = this.querySelector('[data-sticky-button-label]');
      this.ratingContainer = this.querySelector('.product-sticky-buy__rating');
      this.defaultAvailableButtonLabel = this.buttonLabel?.textContent.trim() || '';
      this.variants = this.readVariants();

      this.handleActionClick = this.handleActionClick.bind(this);
      this.handleStickyVariantChange = this.handleStickyVariantChange.bind(this);
      this.handleMobilePickerToggle = this.handleMobilePickerToggle.bind(this);
      this.handleMobileVariantClick = this.handleMobileVariantClick.bind(this);
      this.handleMobilePickerKeydown = this.handleMobilePickerKeydown.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleMainVariantChange = this.handleMainVariantChange.bind(this);
      this.handleProductInfoLoaded = this.handleProductInfoLoaded.bind(this);
      this.handleReviewsUpdated = this.handleReviewsUpdated.bind(this);
      this.handleMainButtonIntersection = this.handleMainButtonIntersection.bind(this);
      this.handleFooterIntersection = this.handleFooterIntersection.bind(this);

      this.actionButton?.addEventListener('click', this.handleActionClick);
      this.variantSelect?.addEventListener('change', this.handleStickyVariantChange);
      this.optionSelects.forEach((select) => select.addEventListener('change', this.handleStickyVariantChange));
      this.mobilePickerToggle?.addEventListener('click', this.handleMobilePickerToggle);
      this.mobileVariantButtons.forEach((button) => button.addEventListener('click', this.handleMobileVariantClick));
      this.mobilePicker?.addEventListener('keydown', this.handleMobilePickerKeydown);
      document.addEventListener('click', this.handleDocumentClick);
      this.productForm?.addEventListener('variant:change', this.handleMainVariantChange);
      document.addEventListener('productInfo:loaded', this.handleProductInfoLoaded);
      document.addEventListener('theme:product-reviews-updated', this.handleReviewsUpdated);
      document.addEventListener('shopify:section:load', this.handleReviewsUpdated);
      document.addEventListener('shopify:section:unload', this.handleReviewsUpdated);

      this.initializeVisibilityObservers();
      this.syncButtonPrice();
      this.refreshReviews();
    }

    /** Remove listeners e observadores quando a seção é recarregada pelo editor Shopify. */
    disconnectedCallback() {
      this.actionButton?.removeEventListener('click', this.handleActionClick);
      this.variantSelect?.removeEventListener('change', this.handleStickyVariantChange);
      this.optionSelects?.forEach((select) => select.removeEventListener('change', this.handleStickyVariantChange));
      this.mobilePickerToggle?.removeEventListener('click', this.handleMobilePickerToggle);
      this.mobileVariantButtons?.forEach((button) => button.removeEventListener('click', this.handleMobileVariantClick));
      this.mobilePicker?.removeEventListener('keydown', this.handleMobilePickerKeydown);
      document.removeEventListener('click', this.handleDocumentClick);
      this.productForm?.removeEventListener('variant:change', this.handleMainVariantChange);
      document.removeEventListener('productInfo:loaded', this.handleProductInfoLoaded);
      document.removeEventListener('theme:product-reviews-updated', this.handleReviewsUpdated);
      document.removeEventListener('shopify:section:load', this.handleReviewsUpdated);
      document.removeEventListener('shopify:section:unload', this.handleReviewsUpdated);
      this.mainButtonObserver?.disconnect();
      this.footerObserver?.disconnect();
    }

    /** Lê os dados de variantes emitidos pelo Liquid sem depender de requisição adicional. */
    readVariants() {
      const variantsScript = this.querySelector('[data-sticky-variants]');

      // Sem o JSON da seção, o seletor permanece visual, mas nenhuma sincronização é tentada.
      if (!variantsScript) return [];

      try {
        return JSON.parse(variantsScript.textContent);
      }
      catch (error) {
        console.error('Não foi possível ler as variantes do sticky buy.', error);
        return [];
      }
    }

    /** Observa o botão principal e o rodapé para reproduzir o comportamento sticky original. */
    initializeVisibilityObservers() {
      const formId = this.getAttribute('form');
      const nestedRegularButton = this.productForm?.querySelector('[name="add"]');
      const associatedRegularButton = Array.from(document.querySelectorAll('[name="add"]')).find((button) => {
        // O botão principal pode ficar fora do elemento form e se associar a ele pelo atributo HTML form.
        return button !== this.actionButton && button.getAttribute('form') === formId;
      });
      const regularButton = nestedRegularButton || associatedRegularButton;

      // No modo condicional, o sticky surge somente depois que o botão comum passa pelo topo.
      if (this.dataset.displayMode !== 'always' && regularButton) {
        this.mainButtonObserver = new IntersectionObserver(this.handleMainButtonIntersection);
        this.mainButtonObserver.observe(regularButton);
      }
      else {
        this.mainButtonPassed = true;
      }

      // O rodapé continua sendo o limite inferior para evitar sobreposição permanente.
      if (this.footer) {
        this.footerObserver = new IntersectionObserver(this.handleFooterIntersection);
        this.footerObserver.observe(this.footer);
      }

      this.updateVisibility();
    }

    /** Registra se o botão comum já ficou acima da viewport. */
    handleMainButtonIntersection(entries) {
      const entry = entries[0];

      // O cálculo usa a borda inferior para só ativar após o botão inteiro desaparecer acima.
      if (entry) this.mainButtonPassed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      this.updateVisibility();
    }

    /** Registra quando o rodapé entra na viewport. */
    handleFooterIntersection(entries) {
      const entry = entries[0];

      // Sem entrada válida, o estado anterior é preservado.
      if (entry) this.footerReached = entry.isIntersecting;
      this.updateVisibility();
    }

    /** Aplica a visibilidade final combinando o modo escolhido e o limite do rodapé. */
    updateVisibility() {
      const shouldShow = Boolean(this.mainButtonPassed) && !this.footerReached;
      this.classList.toggle('is-visible', shouldShow);
    }

    /** Executa a ação configurada sem interferir no submit nativo de adicionar ao carrinho. */
    handleActionClick(event) {
      // A compra é processada pelo ProductForm original; nenhuma ação paralela deve ocorrer.
      if (this.dataset.buttonFunction === 'add_to_cart') return;

      event.preventDefault();
      const target = this.findScrollTarget();

      // Um destino ausente não deve gerar erro nem deslocar o comprador para uma área incorreta.
      if (!target) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
    }

    /** Resolve primeiro o seletor personalizado e depois os destinos conhecidos do tema e apps. */
    findScrollTarget() {
      const customSelector = this.dataset.scrollSelector?.trim();

      // O seletor informado pelo lojista tem prioridade sobre os fallbacks internos.
      if (customSelector) {
        try {
          const customTarget = document.querySelector(customSelector);

          // Um seletor válido e encontrado encerra a busca imediatamente.
          if (customTarget) return customTarget;
        }
        catch (error) {
          console.warn('O seletor personalizado do sticky buy é inválido.', error);
        }
      }

      const selectorsByFunction = {
        scroll_product_info: '.product__info',
        scroll_variant_picker: '.product__info .variant-picker',
        scroll_quantity_breaks: '[data-kaching-bundles], .kaching-bundles, [class*="kaching"][class*="bundle"], [id*="quantity-break"]',
        scroll_pricing_table: '[data-pricing-table], .pricing-table, [id*="pricing-table"], [class*="pricing-table"]'
      };
      const selector = selectorsByFunction[this.dataset.buttonFunction];

      // Funções sem destino cadastrado não devem consultar o DOM com valor indefinido.
      if (!selector) return null;
      return document.querySelector(selector);
    }

    /** Converte alterações do seletor sticky na variante equivalente do produto. */
    handleStickyVariantChange() {
      let selectedVariant;

      // O modo combinado fornece diretamente o ID da variante escolhida.
      if (this.variantSelect) {
        selectedVariant = this.variants.find((variant) => String(variant.id) === this.variantSelect.value);
      }
      else {
        const selectedOptions = this.optionSelects.map((select) => select.value);
        selectedVariant = this.variants.find((variant) => variant.options.every((option, index) => option === selectedOptions[index]));
      }

      // Combinações inexistentes são ignoradas para manter o formulário em uma variante válida.
      if (!selectedVariant) return;
      this.syncVariantToMainPicker(selectedVariant);
    }

    /** Abre ou fecha somente o seletor móvel, mantendo o foco dentro do sticky. */
    handleMobilePickerToggle(event) {
      event.preventDefault();
      event.stopPropagation();

      // O botão próprio não deve substituir o comportamento dos selects no desktop.
      if (!window.matchMedia('(max-width: 767px)').matches) return;
      this.setMobilePickerOpen(!this.mobilePicker?.classList.contains('is-open'));
    }

    /** Seleciona a variante móvel sem disparar o picker principal nem rolar a página. */
    handleMobileVariantClick(event) {
      event.preventDefault();
      event.stopPropagation();
      const button = event.currentTarget;
      const variant = this.variants.find((item) => String(item.id) === button.dataset.variantId);

      // Opções indisponíveis ou dados ausentes não podem alterar o formulário de compra.
      if (!variant || button.disabled) return;
      this.applyMobileVariant(variant);
      this.setMobilePickerOpen(false, true);
    }

    /** Fecha a lista com Escape e devolve o foco sem deslocar a viewport. */
    handleMobilePickerKeydown(event) {
      // As demais teclas continuam com o comportamento nativo dos botões da lista.
      if (event.key !== 'Escape') return;
      event.preventDefault();
      this.setMobilePickerOpen(false, true);
    }

    /** Fecha a lista quando o comprador toca fora do seletor. */
    handleDocumentClick(event) {
      // Cliques internos são tratados pelos controles do próprio componente.
      if (!this.mobilePicker?.classList.contains('is-open') || this.mobilePicker.contains(event.target)) return;
      this.setMobilePickerOpen(false);
    }

    /** Controla estado visual, acessível e foco do painel móvel animado. */
    setMobilePickerOpen(open, restoreFocus = false) {
      // Produtos sem seletor móvel não precisam executar nenhuma alteração.
      if (!this.mobilePicker || !this.mobilePickerToggle || !this.mobilePickerPanel) return;
      this.mobilePicker.classList.toggle('is-open', open);
      this.mobilePickerToggle.setAttribute('aria-expanded', String(open));
      this.mobilePickerPanel.setAttribute('aria-hidden', String(!open));

      // preventScroll garante que fechar a lista nunca leve o comprador à seção principal.
      if (restoreFocus) this.mobilePickerToggle.focus({ preventScroll: true });
    }

    /** Atualiza apenas o sticky e o ID enviado pelo formulário no fluxo móvel. */
    applyMobileVariant(variant) {
      const selectedButton = this.mobileVariantButtons.find((button) => button.dataset.variantId === String(variant.id));
      const masterInput = this.productForm?.querySelector('[name="id"]');
      const stickyVariantTitle = this.querySelector('.product-sticky-buy__variant-title');
      const stickyVariantImage = this.querySelector('.product-sticky-buy__media img');
      const buttonPrice = this.querySelector('[data-sticky-button-price]');

      // O ID mestre é suficiente para o ProductForm adicionar a variante correta ao carrinho.
      if (masterInput) masterInput.value = variant.id;
      if (this.variantSelect) this.variantSelect.value = String(variant.id);
      this.optionSelects.forEach((select, index) => { select.value = variant.options[index]; });

      // Somente informações pertencentes ao sticky são atualizadas nesta interação móvel.
      if (this.mobilePickerLabel) this.mobilePickerLabel.textContent = variant.title;
      if (stickyVariantTitle) stickyVariantTitle.textContent = variant.title;
      if (buttonPrice && selectedButton?.dataset.variantPrice) buttonPrice.textContent = selectedButton.dataset.variantPrice;

      // A imagem é trocada diretamente no sticky para não depender do picker principal.
      if (stickyVariantImage && selectedButton?.dataset.variantImageSrc) {
        stickyVariantImage.src = selectedButton.dataset.variantImageSrc;
        stickyVariantImage.srcset = selectedButton.dataset.variantImageSrcset || selectedButton.dataset.variantImageSrc;
        stickyVariantImage.alt = selectedButton.dataset.variantImageAlt || '';
      }

      this.mobileVariantButtons.forEach((button) => {
        const selected = button === selectedButton;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-selected', String(selected));
      });

      // O estado do botão acompanha a disponibilidade sem acionar eventos do picker principal.
      if (this.actionButton) this.actionButton.disabled = !variant.available;
      if (this.buttonLabel) {
        this.buttonLabel.textContent = variant.available
          ? (this.dataset.customLabel?.trim() || this.defaultAvailableButtonLabel)
          : (this.buttonLabel.dataset.soldOut || this.buttonLabel.textContent);
      }
    }

    /** Atualiza os controles reais do tema e dispara uma única mudança de variante. */
    syncVariantToMainPicker(variant) {
      const mainPicker = document.querySelector(`variant-radios[form="${this.getAttribute('form')}"]`) ||
        document.querySelector(`variant-selects[form="${this.getAttribute('form')}"]`);

      // Produtos sem seletor visível ainda precisam enviar o ID correto no formulário.
      if (!mainPicker) {
        const masterInput = this.productForm?.querySelector('[name="id"]');

        // O input pode não existir em produtos ou formulários externos não convencionais.
        if (masterInput) masterInput.value = variant.id;
        return;
      }

      const radioGroups = Array.from(mainPicker.querySelectorAll('fieldset'));

      // O picker de botões exige marcar cada radio antes de disparar a mudança final.
      if (radioGroups.length > 0) {
        let changedInput;
        radioGroups.forEach((group, index) => {
          const matchingInput = Array.from(group.querySelectorAll('input')).find((input) => input.value === variant.options[index]);

          // Somente valores realmente existentes no grupo podem ser marcados.
          if (matchingInput) {
            matchingInput.checked = true;
            changedInput = matchingInput;
          }
        });

        // Um único evento evita múltiplas requisições de atualização para a mesma variante.
        if (changedInput) changedInput.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      const selects = Array.from(mainPicker.querySelectorAll('select'));
      selects.forEach((select, index) => {
        select.value = variant.options[index];
      });

      // O último select representa a combinação já completamente atualizada.
      if (selects.length > 0) selects[selects.length - 1].dispatchEvent(new Event('change', { bubbles: true }));
    }

    /** Mantém os controles sticky alinhados quando a variante muda no seletor principal. */
    handleMainVariantChange(event) {
      const variant = event.detail?.variant;

      // Eventos sem variante não possuem estado confiável para sincronização.
      if (!variant) return;

      // No modo combinado, basta selecionar o ID correspondente.
      if (this.variantSelect) this.variantSelect.value = String(variant.id);

      this.optionSelects.forEach((select, index) => {
        select.value = variant.options[index];
      });

      // Mudanças iniciadas fora do sticky também mantêm o rótulo móvel alinhado.
      const mobileButton = this.mobileVariantButtons.find((button) => button.dataset.variantId === String(variant.id));
      if (this.mobilePickerLabel) this.mobilePickerLabel.textContent = variant.title;
      this.mobileVariantButtons.forEach((button) => {
        const selected = button === mobileButton;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-selected', String(selected));
      });
    }

    /** Sincroniza preço interno e rótulo depois que o tema atualiza o fragmento da variante. */
    handleProductInfoLoaded() {
      this.syncButtonPrice();
      this.restoreCustomButtonLabel();
    }

    /** Atualiza o sticky depois que a feature principal recalcula avaliações do produto. */
    handleReviewsUpdated(event) {
      // Eventos de outro produto não devem substituir a nota exibida pelo sticky atual.
      if (event.detail?.productId && event.detail.productId !== String(this.dataset.reviewProductId || '0')) return;
      Promise.resolve().then(() => this.refreshReviews());
    }

    /** Copia nota e quantidade da feature principal e atualiza texto e preenchimento. */
    refreshReviews() {
      const reviewData = this.getCurrentReviewData();
      const rating = this.normalizeReviewRating(reviewData.rating);
      const count = Math.max(0, Math.trunc(Number(reviewData.count) || 0));
      const ratingText = rating.toFixed(1);
      const formatter = new Intl.NumberFormat(this.dataset.reviewLocale || undefined);
      const ratingElements = this.querySelectorAll('[data-sticky-review-rating]');
      const countElements = this.querySelectorAll('[data-sticky-review-count]');
      const customStars = this.querySelector('[data-sticky-review-stars]');
      const themeStars = this.querySelector('.product-sticky-buy__theme-review-stars');
      const hasReviews = Boolean(reviewData.hasReviews) && rating > 0 && count > 0;

      // Sem avaliação válida, estrelas e texto somem juntos para nunca expor placeholders ou zeros.
      if (this.ratingContainer) this.ratingContainer.hidden = !hasReviews;

      // O conteúdo oculto não precisa receber preenchimento ou rótulos de uma avaliação inexistente.
      if (!hasReviews) return;

      // As variáveis podem ser omitidas livremente pelo texto configurado no editor.
      ratingElements.forEach((element) => { element.textContent = ratingText; });
      countElements.forEach((element) => { element.textContent = formatter.format(count); });

      // O layout próprio usa preenchimento proporcional exato à nota atual.
      if (customStars) {
        this.style.setProperty('--sticky-review-fill', `${(rating / 5) * 100}%`);
        customStars.setAttribute('aria-label', this.getReviewRatingLabel(ratingText));
      }

      // O layout global preserva a mesma regra de meia estrela da feature principal.
      if (themeStars) {
        const visualRating = Math.floor(rating * 2) / 2;
        themeStars.style.setProperty('--theme-review-stars-fill', `${this.getThemeReviewFill(visualRating, themeStars)}%`);
        themeStars.setAttribute('aria-label', this.getReviewRatingLabel(ratingText));
      }
    }

    /** Prioriza componentes já calculados e usa os valores Liquid como fallback seguro. */
    getCurrentReviewData() {
      const productId = String(this.dataset.reviewProductId || '0');
      const summary = Array.from(document.querySelectorAll('product-review-summary'))
        .find((element) => String(element.dataset.productId || '0') === productId);

      // O resumo global contém exatamente os valores formatados apresentados ao comprador.
      if (summary) {
        const summaryRating = Number(summary.dataset.rating);
        const summaryCountElement = summary.querySelector('[data-review-count]');
        const summaryCountText = summaryCountElement?.textContent || '';
        const summaryCount = Number(summaryCountText.replace(/[^0-9-]/g, ''));

        // Valores válidos encerram a busca e garantem igualdade com o resumo principal.
        if (summaryCountElement && Number.isFinite(summaryRating) && Number.isFinite(summaryCount)) {
          return {
            rating: summaryRating,
            count: summaryCount,
            hasReviews: !summary.hidden && summaryRating > 0 && summaryCount > 0
          };
        }
      }

      const reviewSection = Array.from(document.querySelectorAll('theme-product-reviews'))
        .find((element) => String(element.dataset.productId || '0') === productId);

      // A seção completa expõe os mesmos valores finais mesmo quando não existe bloco de resumo.
      if (reviewSection && Number.isFinite(Number(reviewSection.dataset.rating)) && Number.isFinite(Number(reviewSection.dataset.count))) {
        const sectionRating = Number(reviewSection.dataset.rating);
        const sectionCount = Number(reviewSection.dataset.count);
        return {
          rating: sectionRating,
          count: sectionCount,
          hasReviews: !reviewSection.hidden && sectionRating > 0 && sectionCount > 0
        };
      }

      const initialRating = Number(this.dataset.reviewInitialRating) || 0;
      const initialCount = Number(this.dataset.reviewInitialCount) || 0;
      return {
        rating: initialRating,
        count: initialCount,
        hasReviews: initialRating > 0 && initialCount > 0
      };
    }

    /** Mantém qualquer nota recebida dentro do intervalo permitido de zero a cinco. */
    normalizeReviewRating(value) {
      const rating = Number(value);
      return Math.min(5, Math.max(0, Number.isFinite(rating) ? rating : 0));
    }

    /** Calcula o recorte do SVG global respeitando geometrias Trust e comum. */
    getThemeReviewFill(rating, stars) {
      const fullStars = Math.floor(rating);
      const hasHalfStar = rating - fullStars >= 0.5;
      const commonLayout = stars.classList.contains('theme-review-stars--common');
      const starWidth = commonLayout ? 18 : 96;
      const gap = commonLayout ? 4 : 8;
      const totalWidth = commonLayout ? 106 : 512;
      let activeExtent = fullStars > 0 ? (fullStars * starWidth) + ((fullStars - 1) * gap) : 0;

      // A meia estrela inclui o espaço anterior somente quando já existe estrela cheia.
      if (hasHalfStar && fullStars < 5) activeExtent += (fullStars > 0 ? gap : 0) + (starWidth / 2);
      return (activeExtent / totalWidth) * 100;
    }

    /** Aplica ao sticky o mesmo modelo de rótulo acessível selecionado nas avaliações. */
    getReviewRatingLabel(ratingText) {
      return (this.dataset.reviewRatingLabel || '[rating] / 5').replace('[rating]', ratingText);
    }

    /** Copia somente o preço atual para dentro do botão, sem duplicar HTML complexo. */
    syncButtonPrice() {
      const source = this.querySelector('.product-sticky-buy__price .price__regular');
      const destination = this.querySelector('[data-sticky-button-price]');

      // O preço interno é opcional e depende da existência das duas pontas.
      if (source && destination) destination.textContent = source.textContent.trim();
    }

    /** Restaura o texto personalizado sem substituir mensagens de indisponibilidade. */
    restoreCustomButtonLabel() {
      const customLabel = this.dataset.customLabel?.trim();
      const label = this.querySelector('[data-sticky-button-label]');

      // Rótulo vazio preserva a tradução automática do tema.
      if (!customLabel || !label) return;

      // Botão desabilitado deve continuar mostrando o estado definido pelo tema.
      if (this.actionButton?.disabled) return;
      label.textContent = customLabel;
    }
  }

  customElements.define('product-sticky-buy-button', ProductStickyBuyButton);
}
